import { useEffect, useMemo, useRef, useState, type Dispatch, type SetStateAction } from "react";

import {
  connectProjectDomain,
  inspectProjectDomain,
  listProjectDomains,
  removeProjectDomain,
  retryProjectDomain,
  type ProjectCustomDomain,
} from "../../lib/account-client";

export interface ProjectDomainSettingsApi {
  listProjectDomains: typeof listProjectDomains;
  connectProjectDomain: typeof connectProjectDomain;
  inspectProjectDomain: typeof inspectProjectDomain;
  retryProjectDomain: typeof retryProjectDomain;
  removeProjectDomain: typeof removeProjectDomain;
}

export function ProjectDomainSettings({ workspaceId, projectId, api, onUsageChange, onConnectedDomainChange }: {
  workspaceId: string;
  projectId: string;
  api: ProjectDomainSettingsApi;
  onUsageChange?: (difference: number) => void;
  onConnectedDomainChange?: (projectId: string, hostname: string | null) => void;
}) {
  const [domains, setDomains] = useState<ProjectCustomDomain[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [hostname, setHostname] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const snapshotGeneration = useRef(0);

  useEffect(() => {
    let active = true;
    const generation = ++snapshotGeneration.current;
    void api.listProjectDomains(workspaceId, projectId)
      .then((items) => {
        if (active && generation === snapshotGeneration.current) {
          setDomains(items);
          setLoaded(true);
        }
      })
      .catch(() => undefined);
    return () => { active = false; };
  }, [api, projectId, workspaceId]);

  const connectedHostname = useMemo(
    () => domains.find((domain) => domain.lifecycle === "CONNECTED")?.hostname ?? null,
    [domains],
  );

  useEffect(() => {
    if (loaded) onConnectedDomainChange?.(projectId, connectedHostname);
  }, [connectedHostname, loaded, onConnectedDomainChange, projectId]);

  const pollingKey = useMemo(
    () => domains.filter((domain) => shouldPoll(domain.lifecycle)).map((domain) => domain.id).sort().join("\u0000"),
    [domains],
  );

  useEffect(() => {
    const pollingIds = pollingKey ? pollingKey.split("\u0000") : [];
    if (pollingIds.length === 0) return undefined;
    let active = true;
    let timer: number | undefined;
    const poll = async () => {
      const generation = snapshotGeneration.current;
      await runBounded(pollingIds, 4, async (domainId) => {
        const domain = await api.inspectProjectDomain(workspaceId, projectId, domainId);
        if (active && generation === snapshotGeneration.current) updateDomain(setDomains, domain);
      });
      if (active) timer = window.setTimeout(() => { void poll(); }, 4_000);
    };
    timer = window.setTimeout(() => { void poll(); }, 4_000);
    return () => {
      active = false;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [api, pollingKey, projectId, workspaceId]);

  const connect = async () => {
    snapshotGeneration.current += 1;
    setSaving(true);
    setError(null);
    try {
      const result = await api.connectProjectDomain(workspaceId, projectId, hostname);
      setDomains((current) => [result.domain, ...current.filter((item) => item.id !== result.domain.id)]);
      onUsageChange?.(1);
      setHostname("");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Doorway could not connect this domain.");
    } finally {
      setSaving(false);
    }
  };

  const checkAgain = async (domain: ProjectCustomDomain) => {
    snapshotGeneration.current += 1;
    setBusyId(domain.id);
    setError(null);
    try {
      const result = domain.lifecycle === "FAILED" || domain.lifecycle === "REMOVAL_FAILED"
        ? await api.retryProjectDomain(workspaceId, projectId, domain.id)
        : { outcome: "updated" as const, domain: await api.inspectProjectDomain(workspaceId, projectId, domain.id) };
      if (result.outcome === "removed") {
        setDomains((current) => current.filter((item) => item.id !== domain.id));
        onUsageChange?.(-1);
      } else {
        updateDomain(setDomains, result.domain);
      }
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Doorway could not check this domain.");
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (domain: ProjectCustomDomain) => {
    snapshotGeneration.current += 1;
    setBusyId(domain.id);
    setError(null);
    try {
      await api.removeProjectDomain(workspaceId, projectId, domain.id);
      setDomains((current) => current.filter((item) => item.id !== domain.id));
      onUsageChange?.(-1);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Doorway could not remove this domain.");
      try {
        updateDomain(setDomains, await api.inspectProjectDomain(workspaceId, projectId, domain.id));
      } catch {
        // Keep the current row so the user can retry removal.
      }
    } finally {
      setBusyId(null);
    }
  };

  return (
    <details className="project-domain-settings">
      <summary>Custom domain</summary>
      <p className="domain-setup-intro">Enter a root domain, www address, or subdomain. Doorway creates the hostname, checks DNS, and manages SSL.</p>
      <form onSubmit={(event) => { event.preventDefault(); void connect(); }}>
        <label>
          <span>Domain</span>
          <input
            required
            placeholder="app.example.com"
            autoCapitalize="none"
            autoCorrect="off"
            value={hostname}
            onChange={(event) => setHostname(event.target.value)}
            disabled={saving}
          />
        </label>
        <button className="secondary-button" type="submit" disabled={saving}>{saving ? "Connecting..." : "Connect"}</button>
      </form>
      <p className="domain-root-note">Root domains such as example.com require CNAME flattening or ALIAS/ANAME support from your DNS provider. Subdomains and www work with a standard CNAME.</p>
      {error && <p className="error-message" role="alert">{error}</p>}
      {domains.length === 0 && <p className="workspace-empty">No custom domains are connected.</p>}
      {domains.map((domain) => (
        <DomainCard
          key={domain.id}
          domain={domain}
          busy={busyId === domain.id}
          onCheckAgain={() => void checkAgain(domain)}
          onRemove={() => void remove(domain)}
        />
      ))}
    </details>
  );
}

async function runBounded<T>(items: readonly T[], concurrency: number, operation: (item: T) => Promise<void>): Promise<void> {
  let nextIndex = 0;
  const workers = Array.from({ length: Math.min(concurrency, items.length) }, async () => {
    while (nextIndex < items.length) {
      const item = items[nextIndex++]!;
      await operation(item).catch(() => undefined);
    }
  });
  await Promise.all(workers);
}

function DomainCard({ domain, busy, onCheckAgain, onRemove }: {
  domain: ProjectCustomDomain;
  busy: boolean;
  onCheckAgain(): void;
  onRemove(): void;
}) {
  const verification = verificationRecord(domain.ownershipVerification);
  const stage = stageFor(domain.lifecycle);
  return (
    <article className="project-domain-row">
      <div className="project-domain-heading">
        <span><strong>{domain.hostname}</strong><small>{stage.label}</small></span>
        <span className={`project-status project-${domain.lifecycle === "CONNECTED" ? "live" : "draft"}`}>{stage.label}</span>
      </div>

      <ol className="domain-progress" aria-label="Domain connection progress">
        {[
          ["Checking DNS", 0],
          ["Issuing SSL", 1],
          ["Connected", 2],
        ].map(([label, index]) => (
          <li className={stage.index >= Number(index) ? "complete" : ""} key={String(label)}>{label}</li>
        ))}
      </ol>

      <div className="domain-copy-grid">
        <CopyRow label="Type" value="CNAME" />
        <CopyRow label="Name" value={domain.hostname} />
        <CopyRow label="Target" value={domain.cnameTarget} />
      </div>

      {verification && (
        <div className="domain-copy-grid domain-verification-grid">
          <p>Add this extra ownership record if Cloudflare requests it.</p>
          <CopyRow label="Type" value={verification.type} />
          <CopyRow label="Name" value={verification.name} />
          <CopyRow label="Value" value={verification.value} />
        </div>
      )}

      {(domain.error || domain.lifecycle === "FAILED" || domain.lifecycle === "REMOVAL_FAILED") && (
        <div className="domain-warning" role="alert">
          <strong>Setup needs attention</strong>
          <p>{domain.error?.message ?? "Check the DNS record, then try again."}</p>
          <p>Remove conflicting A, AAAA, or CNAME records. Restrictive CAA records must allow a Cloudflare-supported certificate authority.</p>
        </div>
      )}

      {domain.lifecycle === "CONNECTED" && (
        <p className="domain-connected-note">The hostname, DNS route, and managed SSL certificate are active. Doorway will continue checking certificate health.</p>
      )}

      <div className="project-domain-actions">
        {domain.lifecycle !== "CONNECTED" && domain.lifecycle !== "REMOVAL_PENDING" && (
          <button className="secondary-button" type="button" disabled={busy} onClick={onCheckAgain}>
            {busy ? "Checking..." : domain.lifecycle === "REMOVAL_FAILED" ? "Retry removal" : "Check again"}
          </button>
        )}
        {domain.lifecycle !== "REMOVAL_PENDING" && domain.lifecycle !== "REMOVAL_FAILED" && (
          <button className="text-button" type="button" disabled={busy} onClick={onRemove}>Remove</button>
        )}
      </div>
    </article>
  );
}

function CopyRow({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="dns-copy-row">
      <span>{label}</span>
      <code>{value}</code>
      <button type="button" onClick={() => {
        void navigator.clipboard.writeText(value).then(() => {
          setCopied(true);
          window.setTimeout(() => setCopied(false), 1_500);
        });
      }}>{copied ? "Copied" : "Copy"}</button>
    </div>
  );
}

function updateDomain(
  setDomains: Dispatch<SetStateAction<ProjectCustomDomain[]>>,
  domain: ProjectCustomDomain,
): void {
  setDomains((current) => current.map((item) => item.id === domain.id ? domain : item));
}

function shouldPoll(lifecycle: ProjectCustomDomain["lifecycle"]): boolean {
  return ["PROVISIONING", "CHECKING_DNS", "ISSUING_SSL", "REMOVAL_PENDING"].includes(lifecycle);
}

function stageFor(lifecycle: ProjectCustomDomain["lifecycle"]): { index: number; label: string } {
  if (lifecycle === "CONNECTED") return { index: 2, label: "Connected" };
  if (lifecycle === "ISSUING_SSL") return { index: 1, label: "Issuing SSL" };
  if (lifecycle === "FAILED") return { index: 0, label: "Needs attention" };
  if (lifecycle === "REMOVAL_PENDING") return { index: 0, label: "Removing" };
  if (lifecycle === "REMOVAL_FAILED") return { index: 0, label: "Removal failed" };
  return { index: 0, label: "Checking DNS" };
}

function verificationRecord(value: unknown): { type: string; name: string; value: string } | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  return typeof record.type === "string" && typeof record.name === "string" && typeof record.value === "string"
    ? { type: record.type.toUpperCase(), name: record.name, value: record.value }
    : null;
}
